/**
 * The checklists the old boards file their rounds against (build 7), as the
 * airports pack declares them (backend/packs/airports/checklists/
 * oema-area-rounds.yaml and oema-utility-check.yaml).
 *
 * The item keys are the boards' own: a building or landside round answers its
 * scope's topic keys, and a daily utility check answers `<system>__<check>`.
 * Kept here, pure and with no imports, so the write specs (entry chunk) and the
 * mock source can both read them without loading the demo's tables. The pack
 * is the source; `boardChecklists.test.ts` pins these lists to the boards'
 * tables so the two cannot drift.
 */
import { FIELD_ROUND } from "./inspectionRounds";

export const BUILDING_ROUND = "oema_building_round";
export const LANDSIDE_ROUND = "oema_landside_round";
export const UTILITY_CHECK = "oema_utility_check";

/** The Inspection platform's scope → the checklist its area rounds are filed against. */
export const SCOPE_CHECKLIST: Readonly<Record<string, string>> = {
  airfield: FIELD_ROUND,
  bldg: BUILDING_ROUND,
  landside: LANDSIDE_ROUND,
};

export const BUILDING_ITEMS: readonly string[] = [
  "struct",
  "fire",
  "egress",
  "mech",
  "elec",
  "lifts",
  "clean",
  "access",
  "security",
  "signage",
];

export const LANDSIDE_ITEMS: readonly string[] = [
  "surface",
  "marking",
  "lighting",
  "drain",
  "signs",
  "barrier",
  "ped",
  "clean",
  "traffic",
  "boundary",
];

/** Every daily check of every system, `<system>__<check>`. */
export const UTILITY_ITEMS: readonly string[] = [
  "power__gen_start",
  "power__gen_fuel",
  "power__ups_bat",
  "power__ups_load",
  "power__atc_supply",
  "power__agl_reg",
  "fuel__stock",
  "fuel__water",
  "fuel__filter",
  "fuel__esd",
  "fuel__hydrant",
  "fuel__bonding",
  "hvac__chill",
  "hvac__itroom",
  "hvac__term_t",
  "hvac__humid",
  "hvac__filters",
  "hvac__cond",
  "water__store",
  "water__pressure",
  "water__chlorine",
  "water__lift",
  "water__stp",
  "water__lav",
  "itnet__core",
  "itnet__wan",
  "itnet__backup",
  "itnet__patch",
  "itnet__seg",
  "itnet__events",
  "fire__panel",
  "fire__pump",
  "fire__tank",
  "fire__supp",
  "fire__detect",
  "fire__exting",
  "aodb__aodb_up",
  "aodb__sched",
  "aodb__iface",
  "aodb__rms_alloc",
  "aodb__resp",
  "aodb__aodb_bk",
  "aodb__quality",
  "backbone__feeders",
  "backbone__ring",
  "backbone__commsrm",
  "backbone__commscool",
  "backbone__failover",
  "backbone__spof",
  "backbone__earthing",
  "cupps__cu_plat",
  "cupps__cu_desk",
  "cupps__cu_print",
  "cupps__cu_gate",
  "cupps__cu_app",
  "cupps__cu_stock",
  "bhs__bhs_line",
  "bhs__bhs_jam",
  "bhs__bhs_sort",
  "bhs__bhs_mis",
  "bhs__bhs_scr",
  "bhs__bhs_ems",
  "hbs__hbs_avail",
  "hbs__hbs_cal",
  "hbs__hbs_std",
  "hbs__hbs_rate",
  "hbs__hbs_recon",
  "hbs__hbs_op",
  "screen__sc_lane",
  "screen__sc_xray",
  "screen__sc_body",
  "screen__sc_etd",
  "screen__sc_queue",
  "screen__sc_cert",
  "access__ac_door",
  "access__ac_force",
  "access__ac_pass",
  "access__cc_cam",
  "access__cc_rec",
  "access__cc_blind",
];

/** A system's check → the checklist's item. */
export const utilityItem = (system: string, check: string): string => `${system}__${check}`;
